import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  Collapse,
  InputLabel,
  Tooltip,
} from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import TagIcon from '@mui/icons-material/Tag';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountPickerMulti from '../components/AccountPickerMulti';
import type { AccountGroup } from '../components/AccountPickerMulti';
import { useFieldValidation } from '../hooks/useFieldValidation';
import type { FieldLimit, PairRule } from '../hooks/useFieldValidation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';

// ==== Границы полей: 1-в-1 из StartStoryMentionsCommand и вложенных конфигов ====
// Где в модели у кортежей нет ge/le — ставим разумный минимум без выдуманного потолка.
const SM_LIMITS: Record<string, FieldLimit> = {
  // StorySource.use_last_n_stories (без ge/le)
  useLastNStories: { min: 1, integer: true },

  // MentionsConfig.mentions_per_story: Tuple (без ge/le)
  mentionsPerStoryMin: { min: 1, integer: true },
  mentionsPerStoryMax: { min: 1, integer: true },

  // ProgressiveMentions
  startMentions: { min: 1, max: 30, integer: true },   // ge=1 le=30
  maxMentions: { min: 1, max: 30, integer: true },     // ge=1 le=30
  dailyIncrease: { min: 0.1, max: 10, step: 0.1 },     // ge=0.1 le=10.0 (float)
  daysToMax: { min: 1, max: 60, integer: true },       // ge=1 le=60

  // AutoResponder
  maxResponsesPerUser: { min: 1, max: 20, integer: true }, // ge=1 le=20
  responseDelayMin: { min: 0, integer: true },   // Tuple без ge/le
  responseDelayMax: { min: 0, integer: true },
  followUpDelayMin: { min: 0, integer: true },
  followUpDelayMax: { min: 0, integer: true },
  activeHoursStart: { min: 0, max: 24, integer: true }, // Tuple, логически часы
  activeHoursEnd: { min: 0, max: 24, integer: true },

  // SafetyConfig
  storiesPerAccountDay: { min: 1, max: 20, integer: true }, // ge=1 le=20
  delayBetweenStoriesMin: { min: 0, integer: true },        // Tuple без ge/le
  delayBetweenStoriesMax: { min: 0, integer: true },
  dialogAutoDeleteHours: { min: 1, max: 168, integer: true }, // ge=1 le=168
  dialogCreationDelayMin: { min: 0, integer: true },          // Tuple без ge/le
  dialogCreationDelayMax: { min: 0, integer: true },
  storyDurationHours: { min: 6, max: 168, integer: true },    // ge=6 le=168

  // WorkingHours (0..24)
  workingHoursStart: { min: 0, max: 24, step: 0.5 }, // ge=0 le=24
  workingHoursEnd: { min: 0, max: 24, step: 0.5 },
};

const SM_PAIRS: PairRule[] = [
  { minKey: 'mentionsPerStoryMin', maxKey: 'mentionsPerStoryMax' },
  { minKey: 'startMentions', maxKey: 'maxMentions', message: 'Максимум должен быть ≥ начального' },
  { minKey: 'responseDelayMin', maxKey: 'responseDelayMax' },
  { minKey: 'followUpDelayMin', maxKey: 'followUpDelayMax' },
  { minKey: 'activeHoursStart', maxKey: 'activeHoursEnd', message: 'Конец должен быть позже начала' },
  { minKey: 'delayBetweenStoriesMin', maxKey: 'delayBetweenStoriesMax' },
  { minKey: 'dialogCreationDelayMin', maxKey: 'dialogCreationDelayMax' },
  { minKey: 'workingHoursStart', maxKey: 'workingHoursEnd', message: 'Конец должен быть позже начала' },
];



interface Account {
  id: string;
  name: string;
  phone_number: string;
}

interface ActiveCampaign {
  campaign_id: string;
  name: string;
  account_names: string[];
  status: string;
  recipients_total: number;
  stories_posted: number;
  total_mentions: number;
  unique_users_mentioned: number;
}

const StoryMentions: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [campaigns, setCampaigns] = useState<ActiveCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [tab, setTab] = useState<'setup' | 'active'>('setup');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAutoResponder, setShowAutoResponder] = useState(false);
  const [showProgressive, setShowProgressive] = useState(false);

  const [form, setForm] = useState({
    name: '',
    accountNames: [] as string[],
    recipientsFile: null as File | null,
    storyMode: 'donor_account' as 'donor_account' | 'upload_file',
    donorAccount: '',
    useLastNStories: '1',
    randomize: false,
    storyFile: null as File | null,
    textOverlay: '',
    replaceUrlTag: false,
    urlListFile: null as File | null,
    urlTag: '',
    useAiVariations: false,
    aiPrompt: '',
    mentionsPerStoryMin: '3',
    mentionsPerStoryMax: '5',
    crossAccountDedup: true,
    mentionPosition: 'random' as 'random' | 'top' | 'center' | 'bottom',
    progressiveEnabled: false,
    startMentions: '3',
    maxMentions: '15',
    dailyIncrease: '1.0',
    daysToMax: '10',
    autoResponderEnabled: false,
    triggers: '',
    caseSensitive: false,
    responseMode: 'template' as 'template' | 'ai' | 'both',
    responseTemplates: '',
    aiPromptResponder: '',
    maxResponsesPerUser: '3',
    responseDelayMin: '5',
    responseDelayMax: '30',
    followUpMessages: '',
    followUpDelayMin: '60',
    followUpDelayMax: '300',
    activeHoursStart: '0',
    activeHoursEnd: '24',
    storiesPerAccountDay: '5',
    delayBetweenStoriesMin: '7200',
    delayBetweenStoriesMax: '14400',
    requirePremium: true,
    checkSpamBlock: true,
    uniquifyMedia: true,
    uniquifyMethod: 'both' as 'pixels' | 'metadata' | 'both',
    createDialogsBefore: true,
    dialogAutoDeleteHours: '24',
    dialogCreationDelayMin: '1',
    dialogCreationDelayMax: '3',
    storyPrivacy: 'everyone' as 'everyone' | 'contacts' | 'close_friends',
    storyDurationHours: '48',
    workingHoursEnabled: false,
    workingHoursStart: '9.0',
    workingHoursEnd: '21.0',
    timezone: 'Europe/Moscow',
  });

  const update = (key: keyof typeof form, value: any) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const validation = useFieldValidation({ limits: SM_LIMITS, pairs: SM_PAIRS });

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    const interval = setInterval(fetchActive, 8000);
    return () => clearInterval(interval);
  }, [auth?.isAuthenticated, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const accRes = await api.get('/accounts');
      setAccounts(accRes.data.accounts || []);
      api.get('/account_groups').then(r => setGroups(r.data || [])).catch(() => {});
    } catch (err: any) {
      setMessage({ text: 'Ошибка загрузки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActive = async () => {
    try {
      const res = await api.get('/story_mentions/list');
      setCampaigns(res.data.campaigns || []);
    } catch (err) {
      console.error(err);
    }
  };

const startCampaign = async () => {
  if (!form.name.trim() || form.accountNames.length === 0) {
    setMessage({ text: 'Заполните название и выберите аккаунты', type: 'error' });
    return;
  }
  if (!form.recipientsFile) {
    setMessage({ text: 'Загрузите файл с получателями', type: 'error' });
    return;
  }
  if (form.storyMode === 'donor_account' && !form.donorAccount.trim()) {
    setMessage({ text: 'Укажите донор-аккаунт', type: 'error' });
    return;
  }
  if (form.storyMode === 'upload_file' && !form.storyFile) {
    setMessage({ text: 'Загрузите файл для сториз', type: 'error' });
    return;
  }

  if (!validation.validateAll(form)) {
    setMessage({ text: 'Проверьте выделенные поля — есть ошибки валидации', type: 'error' });
    return;
  }

  setIsLoading(true);

  const formData = new FormData();

  // Простые поля
  formData.append('name', form.name.trim());

  // account_names — каждый отдельно
  form.accountNames.forEach(name => {
    formData.append('account_names', name);
  });

  // recipients_source — только файл по плоскому ключу (recipients_file)
  if (form.recipientsFile) {
    formData.append('recipients_file', form.recipientsFile);
  }

  // story_source — режим + файл по плоскому ключу
  formData.append('story_mode', form.storyMode); // плоский ключ для режима
  if (form.storyMode === 'donor_account') {
    formData.append('donor_account', form.donorAccount.trim());
    formData.append('use_last_n_stories', form.useLastNStories);
    formData.append('randomize', form.randomize ? 'true' : 'false');
  } else if (form.storyFile) {
    formData.append('story_file', form.storyFile);
  }

  // content — конфиг как JSON + файл по плоскому ключу
  const contentObj = {
    text_overlay: form.textOverlay.trim() || null,
     url: form.urlTag.trim() || null,
    replace_url_tag: form.replaceUrlTag,
    use_ai_variations: form.useAiVariations,
    ai_prompt: form.aiPrompt.trim() || null,
  };
  formData.append('content', JSON.stringify(contentObj));

  if (form.urlListFile) {
    formData.append('url_list_file', form.urlListFile);
  }

  // mentions_config — как JSON
 const mentionsConfigObj: any = {
  mentions_per_story: [
    Number(form.mentionsPerStoryMin) || 5,
    Number(form.mentionsPerStoryMax) || 10,
  ],
  cross_account_dedup: form.crossAccountDedup,
  mention_position: form.mentionPosition,
};

if (form.progressiveEnabled) {
  mentionsConfigObj.progressive_mentions = {
    enabled: true,
    start_mentions: Number(form.startMentions) || 3,
    max_mentions: Number(form.maxMentions) || 15,
    daily_increase: Number(form.dailyIncrease) || 1.0,
    days_to_max: Number(form.daysToMax) || 10,
  };
}

formData.append('mentions_config', JSON.stringify(mentionsConfigObj));

  // auto_responder — как JSON
  if (form.autoResponderEnabled) {
    const autoResponderObj = {
      enabled: true,
      triggers: form.triggers.trim().split(/[\n,]/).map(t => t.trim()).filter(Boolean),
      case_sensitive: form.caseSensitive,
      response_mode: form.responseMode,
      response_templates: form.responseTemplates.trim().split(/[\n,]/).map(t => t.trim()).filter(Boolean),
      ai_prompt: form.aiPromptResponder.trim() || null,
      max_responses_per_user: Number(form.maxResponsesPerUser) || 3,
      response_delay: [Number(form.responseDelayMin) || 5, Number(form.responseDelayMax) || 30],
      follow_up_messages: form.followUpMessages.trim().split(/[\n,]/).map(t => t.trim()).filter(Boolean),
      follow_up_delay: [Number(form.followUpDelayMin) || 60, Number(form.followUpDelayMax) || 300],
      active_hours: [Number(form.activeHoursStart) || 0, Number(form.activeHoursEnd) || 24],
    };
    formData.append('auto_responder', JSON.stringify(autoResponderObj));
  }

  // safety — как JSON
  const safetyObj = {
    stories_per_account_day: Number(form.storiesPerAccountDay) || 5,
    delay_between_stories: [Number(form.delayBetweenStoriesMin) || 7200, Number(form.delayBetweenStoriesMax) || 14400],
    require_premium: form.requirePremium,
    check_spam_block: form.checkSpamBlock,
    uniquify_media: form.uniquifyMedia,
    uniquify_method: form.uniquifyMethod,
    create_dialogs_before: form.createDialogsBefore,
    dialog_auto_delete_hours: Number(form.dialogAutoDeleteHours) || 24,
    dialog_creation_delay: [Number(form.dialogCreationDelayMin) || 1, Number(form.dialogCreationDelayMax) || 3],
    story_privacy: form.storyPrivacy,
    story_duration_hours: Number(form.storyDurationHours) || 48,
  };
  formData.append('safety', JSON.stringify(safetyObj));
  formData.append(
  'recipients_source',
  JSON.stringify({ source_type: 'file' })
)

formData.append(
  'story_source',
  JSON.stringify({
    mode: form.storyMode,
    donor_account: form.donorAccount,
    use_last_n_stories: Number(form.useLastNStories),
    randomize: form.randomize
  })
)
  // working_hours — как JSON
  if (form.workingHoursEnabled) {
    const workingHoursObj = {
      start: Number(form.workingHoursStart) || 9.0,
      stop: Number(form.workingHoursEnd) || 21.0,
      timezone: form.timezone,
    };
    formData.append('working_hours', JSON.stringify(workingHoursObj));
  }

  try {
    await api.post('/story_mentions/start', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    setMessage({ text: 'Кампания запущена!', type: 'success' });
    setTab('active');
    fetchActive();
  } catch (err: any) {
    const detail = err.response?.data?.detail || err.message;
    setMessage({ text: `Ошибка: ${detail}`, type: 'error' });
  } finally {
    setIsLoading(false);
  }
};

  const isStartDisabled =
    !form.name.trim() ||
    form.accountNames.length === 0 ||
    !form.recipientsFile ||
    (form.storyMode === 'donor_account' && !form.donorAccount.trim()) ||
    (form.storyMode === 'upload_file' && !form.storyFile);

  const stopCampaign = async (id: string) => {
    setIsLoading(true);
    try {
      await api.delete(`/story_mentions/stop/${id}`);
      setMessage({ text: 'Остановлено', type: 'info' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const restartCampaign = async (id: string) => {
    setIsLoading(true);
    try {
      await api.get(`/story_mentions/restart/${id}`);
      setMessage({ text: 'Перезапущено', type: 'success' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCampaign = async (id: string) => {
    if (!window.confirm('Удалить навсегда?')) return;
    setIsLoading(true);
    try {
      await api.get(`/story_mentions/delete/${id}`);
      setMessage({ text: 'Удалено', type: 'success' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const getTabStyle = (current: 'setup' | 'active') => ({
    flex: 1,
    py: 2,
    borderRadius: 0,
    bgcolor: tab === current ? '#E0F2FE' : 'transparent',
    color: tab === current ? '#1E40AF' : '#6B7280',
    fontWeight: tab === current ? 600 : 400,
    '&:hover': { bgcolor: tab === current ? '#DBEAFE' : '#F3F4F6' },
  });

  if (isLoading && accounts.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#F8FAFC', borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <TagIcon sx={{ fontSize: 40, color: '#1E40AF' }} />
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A8A', mb: 1 }}>
              Отметки в сториз
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="Упоминания в сториз" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Автоответчик" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Обход лимитов" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
            </Box>
            <Typography sx={{ color: '#4B5563', fontSize: '0.95rem', lineHeight: 1.6 }}>
              Автоматическая публикация сториз с упоминаниями пользователей. Поддержка AI, автоответчика и выкладывание сториз через сервис
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Как работают отметки в сториз?
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Система берёт контент (из донора или вашего файла), публикует сториз и отмечает пользователей из списка.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Контент</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              • Донор-аккаунт (последние N сториз)<br />
              • Загруженный файл<br />
              • Текст оверлей, URL-тэг, AI вариации текста
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Упоминания</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              • 1-5 упоминаний на сториз<br />
              • Выбор позиции для URL-тэга и возможность сделать сториз полностью кликабельной<br />
              • Прогрессивное увеличение лимтов
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Вовлечение</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              • Автоответчик на сообщения<br />
              • Триггеры и шаблоны<br />
              • Follow-up сообщения
            </Typography>
          </Box>
        </Box>
         <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Используйте этот метод в качестве альтернативы рассылкам и инвайтингу
        </Typography>
            <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Ознакомьтесь с документацией по автоматизации{' '}
          <a
              href="https://typhon.gitbook.io/docs/automatizations_telegram/story_mentions"
              style={{textDecoration: 'underline', cursor: 'pointer', color: 'inherit'}}
              target="_blank"
              rel="noopener noreferrer"
          >
            здесь
          </a>
          </Typography>
      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
          <Button onClick={() => setTab('setup')} sx={getTabStyle('setup')} startIcon={<PlayArrowIcon />}>
            Новая кампания
          </Button>
          <Button onClick={() => setTab('active')} sx={getTabStyle('active')} startIcon={<StopIcon />}>
            Мои story-рассыльщики ({campaigns.length})
          </Button>
        </Box>

        <Box sx={{ p: 4 }}>
          {tab === 'setup' && (
            <Grid container spacing={3}>
              {/* Основные поля */}
              <Grid size={12}>
                <TextField label="Название кампании" value={form.name} onChange={e => update('name', e.target.value)} fullWidth required />
              </Grid>

                <Grid size={12}>
                    <Typography variant="subtitle1" gutterBottom>
                        Аккаунты
                        <Tooltip title="Выберите аккаунты для работы с упоминанием через сториз">
                            <InfoIcon fontSize="small" sx={{ml: 1, color: '#94A3B8'}}/>
                        </Tooltip>
                    </Typography>
                    <AccountPickerMulti
                        accounts={accounts}
                        selected={form.accountNames}
                        onChange={(names) => update('accountNames', names)}
                        groups={groups}
                    />
                </Grid>

              {/* Получатели */}
              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom>Получатели</Typography>
                <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '2px dashed #CBD5E1' }}>
                  <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                    Загрузить файл с получателями
                    <input type="file" hidden accept=".txt,.csv,.json,.xlsx,.xls,.parquet" onChange={(e: ChangeEvent<HTMLInputElement>) => e.target.files?.[0] && update('recipientsFile', e.target.files[0])} />
                  </Button>
                  {form.recipientsFile && (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachFileIcon color="primary" />
                      <Typography variant="body2">{form.recipientsFile.name}</Typography>
                      <IconButton size="small" onClick={() => update('recipientsFile', null)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Источник сториз */}
              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom>Источник сториз</Typography>
                <FormControl fullWidth>
                  <InputLabel>Режим</InputLabel>
                  <Select value={form.storyMode} onChange={e => update('storyMode', e.target.value as any)} label="Режим">
                    <MenuItem value="donor_account">Донор-аккаунт</MenuItem>
                    <MenuItem value="upload_file">Загрузка файла</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {form.storyMode === 'donor_account' && (
                <>
                  <Grid size={12}>
                    <TextField label="Донор (@username или ID)" value={form.donorAccount} onChange={e => update('donorAccount', e.target.value)} fullWidth />
                  </Grid>
                  <Grid size={6}>
                    <TextField label="Последние N сториз" type="number" {...validation.fieldProps('useLastNStories', form, update)} fullWidth />
                  </Grid>
                  <Grid size={6}>
                    <FormControlLabel control={<Checkbox checked={form.randomize} onChange={e => update('randomize', e.target.checked)} />} label="Рандомизировать" />
                  </Grid>
                </>
              )}

              {form.storyMode === 'upload_file' && (
                <Grid size={12}>
                  <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '2px dashed #CBD5E1' }}>
                    <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                      Загрузить фото/видео
                      <input type="file" hidden accept="image/*,video/*" onChange={(e: ChangeEvent<HTMLInputElement>) => e.target.files?.[0] && update('storyFile', e.target.files[0])} />
                    </Button>
                    {form.storyFile && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon color="primary" />
                        <Typography variant="body2">{form.storyFile.name}</Typography>
                        <IconButton size="small" onClick={() => update('storyFile', null)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Контент */}
              <Grid size={12}>
                <TextField label="Текст поверх сториз" value={form.textOverlay} onChange={e => update('textOverlay', e.target.value)} fullWidth multiline rows={3} />
              </Grid>
              <Grid size={12}>
                <TextField
                    label="URL-тег на сториз (только для Premium аккаунтов)"
                    placeholder="https://t.me/your_channel"
                    value={form.urlTag}
                    onChange={e => update('urlTag', e.target.value)}
                    fullWidth
                    helperText={
                      form.urlTag
                          ? 'Интерактивный тег поверх сториз. Позиция — из настройки «Позиция упоминаний».'
                          : 'Оставьте пустым если URL-тег не нужен'
                    }
                />
              </Grid>
              <Grid size={12}>
                <FormControlLabel control={<Checkbox checked={form.replaceUrlTag} onChange={e => update('replaceUrlTag', e.target.checked)} />} label="Заменять [URL] на ссылки из файла" />
              </Grid>
              {form.replaceUrlTag && (
                <Grid size={12}>
                  <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '2px dashed #CBD5E1' }}>
                    <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                      Файл с URL
                      <input type="file" hidden accept=".txt,.csv,.json,.xlsx,.xls" onChange={(e: ChangeEvent<HTMLInputElement>) => e.target.files?.[0] && update('urlListFile', e.target.files[0])} />
                    </Button>
                    {form.urlListFile && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon color="primary" />
                        <Typography variant="body2">{form.urlListFile.name}</Typography>
                        <IconButton size="small" onClick={() => update('urlListFile', null)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              <Grid size={12}>
                <FormControlLabel control={<Checkbox checked={form.useAiVariations} onChange={e => update('useAiVariations', e.target.checked)} />} label="AI вариации текста" />
              </Grid>
              {form.useAiVariations && (
                <Grid size={12}>
                  <TextField label="Промпт для AI" value={form.aiPrompt} onChange={e => update('aiPrompt', e.target.value)} fullWidth multiline rows={4} />
                </Grid>
              )}

              {/* Упоминания */}
              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom>Упоминания</Typography>
              </Grid>
              <Grid size={6}>
                <TextField label="Мин упоминаний" type="number" {...validation.fieldProps('mentionsPerStoryMin', form, update)}
                           inputProps={{min: 1, max: 5}} fullWidth helperText="от 1 до 5"/>
              </Grid>
              <Grid size={6}>
                <TextField label="Макс упоминаний" type="number" {...validation.fieldProps('mentionsPerStoryMax', form, update)}
                           inputProps={{min: 1, max: 5}} fullWidth helperText="от 1 до 5 (лимит Telegram)"/>
              </Grid>
              <Grid size={12}>
                <FormControlLabel control={<Checkbox checked={form.crossAccountDedup}
                                                     onChange={e => update('crossAccountDedup', e.target.checked)}/>}
                                  label="Дедупликация между аккаунтами"/>
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Позиция url-тэга</InputLabel>
                  <Select value={form.mentionPosition} onChange={e => update('mentionPosition', e.target.value as any)}
                          label="Позиция URL-тэга">
                    <MenuItem value="random">Случайно</MenuItem>
                    <MenuItem value="top">Сверху</MenuItem>
                    <MenuItem value="center">По центру</MenuItem>
                    <MenuItem value="bottom">Снизу</MenuItem>
                    <MenuItem value="fullscreen">Во весь экран</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControlLabel
                    control={<Checkbox checked={form.progressiveEnabled} onChange={e => {
                      update('progressiveEnabled', e.target.checked);
                      setShowProgressive(e.target.checked);
                    }}/>}
                    label="Прогрессивные упоминания"
                />
              </Grid>
              <Collapse in={showProgressive}>
                <Box sx={{pl: 4, bgcolor: '#F8FAFC', p: 3, borderRadius: 2, mb: 3}}>
                  <Grid container spacing={3}>
                    <Grid size={6}>
                      <TextField label="Начальное количество" type="number" {...validation.fieldProps('startMentions', form, update)} fullWidth/>
                    </Grid>
                    <Grid size={6}>
                      <TextField label="Максимум" type="number" {...validation.fieldProps('maxMentions', form, update)} fullWidth/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        label="Увеличение в день"
                        type="number"
                        value={form.dailyIncrease}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Разрешаем пустое поле или валидное число с плавающей точкой
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            update('dailyIncrease', value);
                            validation.validateField('dailyIncrease', { ...form, dailyIncrease: value });
                          }
                        }}
                        onBlur={(e) => {
                          const clamped = validation.clampOnBlur('dailyIncrease', e.target.value);
                          if (clamped !== e.target.value) update('dailyIncrease', clamped);
                          validation.validateField('dailyIncrease', { ...form, dailyIncrease: clamped });
                        }}
                        error={!!validation.errors.dailyIncrease}
                        inputProps={{
                          step: '0.1',
                          min: '0.1',
                          max: '10.0'
                        }}
                        fullWidth
                        helperText={validation.errors.dailyIncrease || 'От 0.1 до 10.0'}
                    />
                    </Grid>
                    <Grid size={6}>
                      <TextField label="Дней до максимума" type="number" {...validation.fieldProps('daysToMax', form, update)} fullWidth />
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>

              {/* Автоответчик */}
              <Grid size={12}>
                <FormControlLabel
                  control={<Checkbox checked={form.autoResponderEnabled} onChange={e => { update('autoResponderEnabled', e.target.checked); setShowAutoResponder(e.target.checked); }} />}
                  label="Автоответчик"
                />
              </Grid>
              <Collapse in={showAutoResponder}>
                <Box sx={{ pl: 4, bgcolor: '#F8FAFC', p: 3, borderRadius: 2, mb: 3 }}>
                  <Grid container spacing={3}>
                    <Grid size={12}>
                      <TextField label="Триггерные слова (через запятую или по одному в строке)" placeholder="слово1, слово2, слово3" value={form.triggers} onChange={e => update('triggers', e.target.value)} fullWidth multiline rows={3} />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.caseSensitive} onChange={e => update('caseSensitive', e.target.checked)} />} label="Учитывать регистр" />
                    </Grid>
                    <Grid size={12}>
                      <FormControl fullWidth>
                        <InputLabel>Режим ответа</InputLabel>
                        <Select value={form.responseMode} onChange={e => update('responseMode', e.target.value as any)} label="Режим ответа">
                          <MenuItem value="template">Шаблон</MenuItem>
                          <MenuItem value="ai">AI</MenuItem>
                          <MenuItem value="both">Оба</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={12}>
                      <TextField label="Шаблоны ответов (через запятую или по одному в строке)" placeholder="слово1, слово2, слово3" value={form.responseTemplates} onChange={e => update('responseTemplates', e.target.value)} fullWidth multiline rows={4} />
                    </Grid>
                    <Grid size={12}>
                      <TextField label="AI промпт" value={form.aiPromptResponder} onChange={e => update('aiPromptResponder', e.target.value)} fullWidth multiline rows={3} />
                    </Grid>
                    <Grid size={6}>
                      <TextField label="Макс ответов на пользователя" type="number" {...validation.fieldProps('maxResponsesPerUser', form, update)} fullWidth />
                    </Grid>
                    <Grid size={3}>
                      <TextField label="Задержка мин (сек)" type="number" {...validation.fieldProps('responseDelayMin', form, update)} fullWidth />
                    </Grid>
                    <Grid size={3}>
                      <TextField label="Задержка макс (сек)" type="number" {...validation.fieldProps('responseDelayMax', form, update)} fullWidth />
                    </Grid>
                    <Grid size={12}>
                      <TextField label="Follow-up сообщения (через запятую или по одному в строке)" placeholder="слово1, слово2, слово3" value={form.followUpMessages} onChange={e => update('followUpMessages', e.target.value)} fullWidth multiline rows={3} />
                    </Grid>
                    <Grid size={3}>
                      <TextField label="Follow-up мин (сек)" type="number" {...validation.fieldProps('followUpDelayMin', form, update)} fullWidth />
                    </Grid>
                    <Grid size={3}>
                      <TextField label="Follow-up макс (сек)" type="number" {...validation.fieldProps('followUpDelayMax', form, update)} fullWidth />
                    </Grid>
                    <Grid size={3}>
                      <TextField label="Активные часы начало" type="number" {...validation.fieldProps('activeHoursStart', form, update)} fullWidth />
                    </Grid>
                    <Grid size={3}>
                      <TextField label="Активные часы конец" type="number" {...validation.fieldProps('activeHoursEnd', form, update)} fullWidth />
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>

              {/* Безопасность */}
              <Grid size={12}>
                <Button variant="outlined" fullWidth onClick={() => setShowAdvanced(!showAdvanced)} endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                  Безопасность
                </Button>
              </Grid>
              <Collapse in={showAdvanced}>
                <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                  <Grid container spacing={3}>
                    <Grid size={12}>
                      <TextField label="Stories на аккаунт в день" type="number" {...validation.fieldProps('storiesPerAccountDay', form, update)} fullWidth />
                    </Grid>
                    <Grid size={6}>
                      <TextField label="Задержка между stories мин (сек)" type="number" {...validation.fieldProps('delayBetweenStoriesMin', form, update)} fullWidth />
                    </Grid>
                    <Grid size={6}>
                      <TextField label="Задержка между stories макс (сек)" type="number" {...validation.fieldProps('delayBetweenStoriesMax', form, update)} fullWidth />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.requirePremium} onChange={e => update('requirePremium', e.target.checked)} />} label="Только Premium аккаунты" />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.checkSpamBlock} onChange={e => update('checkSpamBlock', e.target.checked)} />} label="Проверять spam-блоки" />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.uniquifyMedia} onChange={e => update('uniquifyMedia', e.target.checked)} />} label="Уникализировать медиа" />
                    </Grid>
                    {form.uniquifyMedia && (
                      <Grid size={12}>
                        <FormControl fullWidth>
                          <InputLabel>Метод уникализации</InputLabel>
                          <Select value={form.uniquifyMethod} onChange={e => update('uniquifyMethod', e.target.value as any)} label="Метод уникализации">
                            <MenuItem value="pixels">Пиксели</MenuItem>
                            <MenuItem value="metadata">Метаданные</MenuItem>
                            <MenuItem value="both">Оба</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.createDialogsBefore} onChange={e => update('createDialogsBefore', e.target.checked)} />} label="Создавать диалоги перед упоминанием" />
                    </Grid>
                    <Grid size={12}>
                      <TextField label="Автоудаление диалога (часы)" type="number" {...validation.fieldProps('dialogAutoDeleteHours', form, update)} fullWidth />
                    </Grid>
                    <Grid size={6}>
                      <TextField label="Задержка создания диалогов мин (сек)" type="number" {...validation.fieldProps('dialogCreationDelayMin', form, update)} fullWidth />
                    </Grid>
                    <Grid size={6}>
                      <TextField label="Задержка создания диалогов макс (сек)" type="number" {...validation.fieldProps('dialogCreationDelayMax', form, update)} fullWidth />
                    </Grid>
                    <Grid size={12}>
                      <FormControl fullWidth>
                        <InputLabel>Приватность сториз</InputLabel>
                        <Select value={form.storyPrivacy} onChange={e => update('storyPrivacy', e.target.value as any)} label="Приватность сториз">
                          <MenuItem value="everyone">Все</MenuItem>
                          <MenuItem value="contacts">Контакты</MenuItem>
                          <MenuItem value="close_friends">Близкие друзья</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={12}>
                      <TextField label="Длительность сториз (часы)" type="number" {...validation.fieldProps('storyDurationHours', form, update)} fullWidth />
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>

              {/* Рабочие часы */}
              <Grid size={12}>
                <FormControlLabel control={<Checkbox checked={form.workingHoursEnabled} onChange={e => update('workingHoursEnabled', e.target.checked)} />} label="Ограничить рабочие часы" />
              </Grid>
              {form.workingHoursEnabled && (
                <>
                 <Grid size={6}>
                   <TextField
                       label="Начало (час)"
                       type="number"
                       {...validation.fieldProps('workingHoursStart', form, update)}
                       fullWidth
                       inputProps={{
                         step: '0.5',     // <-- правильно через inputProps
                         min: '0',
                         max: '23.5'
                       }}
                   />
                 </Grid>
                  <Grid size={6}>
                    <TextField
                        label="Конец (час)"
                        type="number"
                        {...validation.fieldProps('workingHoursEnd', form, update)}
                        fullWidth
                        inputProps={{
                          step: '0.5',
                          min: '0.5',
                          max: '24'
                        }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField label="Часовой пояс" value={form.timezone} onChange={e => update('timezone', e.target.value)} fullWidth />
                  </Grid>
                </>
              )}

              <Grid size={12}>
                <Button
                    variant="contained"
                    onClick={startCampaign}
                    disabled={isLoading || isStartDisabled || validation.hasErrors}
                    startIcon={isLoading ? <CircularProgress size={22}/> : <PlayArrowIcon/>}
                    fullWidth
                    sx={{
                      py: 2,
                      fontSize: '1.1rem',
                      bgcolor: '#1E3A8A',
                      '&:hover': {bgcolor: '#1E40AF'}
                    }}
                >
                  {isLoading ? 'Запуск...' : 'Запустить'}
                </Button>
              </Grid>
            </Grid>
          )}

          {/* Активные кампании */}
          {tab === 'active' && (
            campaigns.length === 0 ? (
              <Typography textAlign="center" py={10} color="text.secondary">
                Нет активных кампаний отметок в сториз
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>ID</strong></TableCell>
                      <TableCell><strong>Название</strong></TableCell>
                      <TableCell><strong>Аккаунты</strong></TableCell>
                      <TableCell><strong>Stories</strong></TableCell>
                      <TableCell><strong>Упоминания</strong></TableCell>
                      <TableCell><strong>Уникальные</strong></TableCell>
                      <TableCell><strong>Статус</strong></TableCell>
                      <TableCell align="right"><strong>Действия</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map(c => (
                      <TableRow key={c.campaign_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/story_mentions/detail/${c.campaign_id}`)}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{c.campaign_id.slice(0, 8)}</TableCell>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>{c.account_names.length}</TableCell>
                        <TableCell>{c.stories_posted}</TableCell>
                        <TableCell>{c.total_mentions}</TableCell>
                        <TableCell>{c.unique_users_mentioned}</TableCell>
                        <TableCell>
                          <Chip label={c.status === 'running' ? 'Работает': c.status === 'stopped' ? 'Остановлен': c.status==='completed' ? 'Завершен' : c.status} color={c.status === 'running' ? 'success': c.status==='completed' ? 'success' : 'warning'} />
                        </TableCell>
                        <TableCell align="right" onClick={e => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            {c.status === 'running' && (
                              <IconButton size="small" color="error" onClick={() => stopCampaign(c.campaign_id)}>
                                <StopIcon />
                              </IconButton>
                            )}
                            {c.status !== 'running' && (
                              <IconButton size="small" color="success" onClick={() => restartCampaign(c.campaign_id)}>
                                <PlayArrowIcon />
                              </IconButton>
                            )}
                            <IconButton size="small" color="error" onClick={() => deleteCampaign(c.campaign_id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default StoryMentions;